import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Minus, ShoppingBag, CalendarClock, Search, Newspaper, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { fileUrl } from "@/lib/upload";
import CustomerAccount from "@/components/CustomerAccount";
import { authHeader } from "@/lib/customer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const gbp = (n) => `£${(Number(n) || 0).toFixed(2)}`;

function OptionDialog({ item, primary, onClose, onConfirm }) {
  const [sel, setSel] = useState({});
  const chosen = Object.entries(sel).map(([gid, v]) => {
    const g = item.options.find((x) => x.id === gid);
    return { name: g?.name, value: v.name, value_id: v.id, price_modifier: v.price_modifier || 0 };
  });
  const missing = item.options.some((g) => g.required && !sel[g.id]);
  const price = item.price + chosen.reduce((s, v) => s + (v.price_modifier || 0), 0);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="site-opt-dialog">
      <div className="bg-white rounded-2xl p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <p className="font-display font-bold text-lg mb-3">{item.name}</p>
        {item.options.map((g) => (
          <div key={g.id} className="mb-3">
            <p className="text-sm font-medium mb-1.5">{g.name}{g.required && <span className="text-red-500"> *</span>}</p>
            <div className="flex flex-wrap gap-2">
              {(g.values || []).map((v) => (
                <button key={v.id} data-testid={`site-opt-${v.id}`} onClick={() => setSel((p) => ({ ...p, [g.id]: v }))}
                  className={`h-9 px-3 rounded-lg text-sm border ${sel[g.id]?.id === v.id ? "border-primary bg-primary/10 text-primary" : "border-stone-200"}`}>
                  {v.name}{v.price_modifier ? ` +${gbp(v.price_modifier)}` : ""}
                </button>
              ))}
            </div>
          </div>
        ))}
        <Button disabled={missing} className="w-full h-11 mt-2" style={{ background: primary }} data-testid="site-opt-confirm" onClick={() => onConfirm(chosen, price)}>Add · {gbp(price)}</Button>
      </div>
    </div>
  );
}

export default function PublicSite() {
  const params = useParams();
  const ident = params.rid || params.slug;
  const tableId = new URLSearchParams(window.location.search).get("table");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [basket, setBasket] = useState([]);
  const [fulfil, setFulfil] = useState("COLLECTION");
  const [optItem, setOptItem] = useState(null);
  const [cust, setCust] = useState({ name: "", phone: "", address: "", postcode: "", notes: "", allergy: "" });
  const [booking, setBooking] = useState({ customer_name: "", customer_phone: "", booking_date: "", booking_time: "19:00", guest_count: 2, allergy_dietary_note: "" });
  const [track, setTrack] = useState({ number: "", result: null });
  const [customer, setCustomer] = useState(null);
  const [declaredAllergens, setDeclaredAllergens] = useState([]);

  const basketAllergens = useMemo(() => {
    const set = [];
    basket.forEach((l) => (l.allergens || []).forEach((a) => { if (!set.includes(a)) set.push(a); }));
    return set.sort();
  }, [basket]);
  const toggleDeclared = (name) =>
    setDeclaredAllergens((p) => p.includes(name) ? p.filter((x) => x !== name) : [...p, name]);
  const lineFlagged = (l) => declaredAllergens.some((a) => (l.allergens || []).includes(a));

  const onAuthChange = (c) => {
    setCustomer(c);
    if (c) {
      setCust((p) => ({ ...p, name: p.name || c.name || "", phone: p.phone || c.phone || "" }));
      setBooking((p) => ({ ...p, customer_name: p.customer_name || c.name || "", customer_phone: p.customer_phone || c.phone || "" }));
    }
  };

  useEffect(() => {
    axios.get(`${API}/website/${ident}/site`).then((r) => setData(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Site unavailable"));
  }, [ident]);

  const rid = data?.restaurant?.id;
  const dineMode = !!tableId;

  const primary = data?.branding?.primary_color || "#0a1a3f";
  const add = (m, opts = [], price = m.price) => {
    const key = m.id + "|" + opts.map((o) => o.value_id).sort().join(",");
    setBasket((p) => {
      const e = p.find((x) => x.key === key);
      return e ? p.map((x) => x.key === key ? { ...x, quantity: x.quantity + 1 } : x)
        : [...p, { key, menu_item_id: m.id, name: m.name, price, quantity: 1, options: opts, allergens: m.allergens || [] }];
    });
    setOptItem(null);
  };
  const addItem = (m) => (m.options || []).length > 0 ? setOptItem(m) : add(m);
  const setQty = (key, d) => setBasket((p) => p.map((x) => x.key === key ? { ...x, quantity: Math.max(0, x.quantity + d) } : x).filter((x) => x.quantity > 0));
  const subtotal = basket.reduce((s, l) => s + l.price * l.quantity, 0);
  const fee = (fulfil === "DELIVERY" && basket.length > 0) ? (data?.config?.delivery_fee || 0) : 0;

  const byCat = useMemo(() => {
    if (!data) return [];
    return data.categories.map((c) => ({ ...c, items: data.menu.filter((m) => m.category_id === c.id) })).filter((c) => c.items.length);
  }, [data]);

  const placeOrder = async () => {
    if (basket.length === 0) return toast.error("Your basket is empty");
    if (!cust.name && !dineMode) return toast.error("Enter your name");
    if (!dineMode && fulfil === "DELIVERY" && (!cust.address || !cust.postcode)) return toast.error("Enter delivery address + postcode");
    const items = basket.map((b) => ({ menu_item_id: b.menu_item_id, quantity: b.quantity, options: b.options || [] }));
    try {
      if (dineMode) {
        const { data: res } = await axios.post(`${API}/website/${rid}/table-order`, {
          table_id: tableId, customer_name: cust.name, notes: cust.notes, allergy_dietary_note: cust.allergy,
          declared_allergens: declaredAllergens, items,
        }, { headers: authHeader() });
        toast.success(`Order ${res.order_number} sent to the kitchen! A team member will bring it to your table.`);
        setBasket([]); setDeclaredAllergens([]); setCust({ ...cust, notes: "", allergy: "" });
        return;
      }
      const { data: res } = await axios.post(`${API}/website/${rid}/order`, {
        customer_name: cust.name, customer_phone: cust.phone, fulfillment_type: fulfil,
        delivery_address: cust.address, delivery_postcode: cust.postcode, notes: cust.notes,
        allergy_dietary_note: cust.allergy, declared_allergens: declaredAllergens, items,
      }, { headers: authHeader() });
      if (res.status === "ALLERGY_REVIEW_REQUIRED")
        toast.success(`Order ${res.order_number} received (${gbp(res.total)}). Our team will review your allergy note and confirm shortly.`);
      else
        toast.success(`Order ${res.order_number} placed! Total ${gbp(res.total)}`);
      setBasket([]); setDeclaredAllergens([]); setTrack({ number: res.order_number, result: null });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not place order"); }
  };

  const submitBooking = async () => {
    if (!booking.customer_name || !booking.booking_date) return toast.error("Name and date required");
    try {
      const { data: res } = await axios.post(`${API}/website/${rid}/booking`, { ...booking, guest_count: +booking.guest_count }, { headers: authHeader() });
      toast.success(`Booking requested — ref ${res.booking_ref}`);
      setBooking({ ...booking, customer_name: "", customer_phone: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not book"); }
  };

  const doTrack = async () => {
    if (!track.number) return;
    try { const { data: res } = await axios.get(`${API}/website/${rid}/order/${track.number}`); setTrack((t) => ({ ...t, result: res })); }
    catch { toast.error("Order not found"); }
  };

  if (err) return <div className="min-h-screen flex items-center justify-center text-muted-foreground" data-testid="site-error">{err}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;

  const logo = data.branding.logo, hero = data.branding.hero_image;

  return (
    <div className="min-h-screen bg-stone-50" data-testid="public-site">
      {/* Hero */}
      <header className="relative text-white px-6 py-16 text-center overflow-hidden" style={{ background: primary }}>
        {hero && <img src={fileUrl(hero)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" data-testid="site-hero-image" />}
        <div className="absolute top-4 right-4 z-10">
          <CustomerAccount primary={primary} customer={customer} onAuthChange={onAuthChange} />
        </div>
        <div className="relative">
          {logo && <img src={fileUrl(logo)} alt={data.restaurant.name} className="h-20 w-20 mx-auto mb-4 rounded-2xl object-cover ring-2 ring-white/30 bg-white/10" data-testid="site-logo" />}
          <h1 className="font-display text-4xl sm:text-5xl font-bold" data-testid="site-name">{data.restaurant.name}</h1>
          <p className="mt-3 text-white/80 max-w-xl mx-auto">{data.branding.tagline}</p>
          {dineMode
            ? <p className="mt-3 inline-block rounded-full bg-white/15 px-4 py-1.5 text-sm font-medium" data-testid="dine-banner">Ordering to your table · order will be brought to you</p>
            : (data.restaurant.phone && <p className="mt-2 text-white/70 text-sm">Call us: {data.restaurant.phone}</p>)}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-10 grid lg:grid-cols-[1fr_360px] gap-8 items-start">
        <div className="space-y-10">
          {/* Newsfeed */}
          {data.newsfeed.length > 0 && (
            <section data-testid="site-newsfeed">
              <h2 className="font-display text-2xl font-bold mb-3 flex items-center gap-2"><Newspaper className="h-5 w-5" /> What's on</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {data.newsfeed.map((p) => {
                  const imgs = (p.images && p.images.length ? p.images : (p.image_url ? [p.image_url] : []));
                  return (
                    <a key={p.id} href={p.url || "#"} target={p.url ? "_blank" : undefined} rel="noreferrer"
                      data-testid={`news-${p.id}`} className="block rounded-xl border border-stone-200 bg-white overflow-hidden hover:shadow-md transition-shadow">
                      {imgs.length > 0 && (
                        <div className={`grid ${imgs.length > 1 ? "grid-cols-2" : "grid-cols-1"} gap-0.5`}>
                          {imgs.slice(0, 4).map((im, i) => <img key={i} src={fileUrl(im)} alt="" className="w-full h-32 object-cover" />)}
                        </div>
                      )}
                      <div className="p-4">
                        <p className="font-semibold flex items-center gap-1">{p.title}{p.url && <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />}</p>
                        {p.body && <p className="text-sm text-muted-foreground mt-1">{p.body}</p>}
                      </div>
                    </a>
                  );
                })}
              </div>
            </section>
          )}

          {/* Menu */}
          <section data-testid="site-menu">
            <h2 className="font-display text-2xl font-bold mb-3">Menu</h2>
            {byCat.map((c) => (
              <div key={c.id} className="mb-6">
                <h3 className="font-semibold text-lg mb-2" style={{ color: primary }}>{c.name}</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {c.items.map((m) => (
                    <div key={m.id} data-testid={`site-item-${m.id}`} className="flex items-start justify-between rounded-xl border border-stone-200 bg-white p-4">
                      <div className="min-w-0 pr-3">
                        <p className="font-medium">{m.name} {m.is_popular && <span className="text-[10px] rounded-full bg-amber-100 text-amber-700 px-1.5">Popular</span>}
                          {(m.options || []).length > 0 && <span className="text-[10px] ml-1" style={{ color: primary }}>options</span>}</p>
                        {m.description && <p className="text-sm text-muted-foreground mt-0.5">{m.description}</p>}
                        <p className="font-semibold mt-1">{gbp(m.price)}</p>
                      </div>
                      <Button size="sm" className="shrink-0" style={{ background: primary }} data-testid={`site-add-${m.id}`} onClick={() => addItem(m)}><Plus className="h-4 w-4" /></Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </section>

          {/* Booking */}
          {!dineMode && data.config.table_booking_enabled && (
            <section data-testid="site-booking" className="rounded-2xl border border-stone-200 bg-white p-6">
              <h2 className="font-display text-2xl font-bold mb-3 flex items-center gap-2"><CalendarClock className="h-5 w-5" /> Book a table</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input placeholder="Your name" value={booking.customer_name} onChange={(e) => setBooking({ ...booking, customer_name: e.target.value })} data-testid="book-name" />
                <Input placeholder="Phone" value={booking.customer_phone} onChange={(e) => setBooking({ ...booking, customer_phone: e.target.value })} data-testid="book-phone" />
                <Input type="date" value={booking.booking_date} onChange={(e) => setBooking({ ...booking, booking_date: e.target.value })} data-testid="book-date" />
                <Input placeholder="Time e.g. 19:00" value={booking.booking_time} onChange={(e) => setBooking({ ...booking, booking_time: e.target.value })} data-testid="book-time" />
                <Input type="number" min={1} placeholder="Guests" value={booking.guest_count} onChange={(e) => setBooking({ ...booking, guest_count: e.target.value })} data-testid="book-guests" />
                <Input placeholder="Allergies / notes" value={booking.allergy_dietary_note} onChange={(e) => setBooking({ ...booking, allergy_dietary_note: e.target.value })} data-testid="book-allergy" />
              </div>
              <Button className="mt-3 h-11" style={{ background: primary }} onClick={submitBooking} data-testid="book-submit">Request booking</Button>
            </section>
          )}

          {/* Track order */}
          {!dineMode && (
          <section className="rounded-2xl border border-stone-200 bg-white p-6" data-testid="site-track">
            <h2 className="font-display text-2xl font-bold mb-3 flex items-center gap-2"><Search className="h-5 w-5" /> Track your order</h2>
            <div className="flex gap-2">
              <Input placeholder="Order number e.g. ORD-XXXXXX" value={track.number} onChange={(e) => setTrack({ ...track, number: e.target.value })} data-testid="track-input" />
              <Button className="h-11" variant="outline" onClick={doTrack} data-testid="track-btn">Track</Button>
            </div>
            {track.result && <p className="mt-3 text-sm" data-testid="track-result">Order {track.result.order_number}: <b>{track.result.status.replaceAll("_", " ")}</b> · {gbp(track.result.total)}</p>}
          </section>
          )}
        </div>

        {/* Basket */}
        <aside className="rounded-2xl border border-stone-200 bg-white p-5 lg:sticky lg:top-6" data-testid="site-basket">
          <h2 className="font-display text-xl font-bold flex items-center gap-2 mb-3"><ShoppingBag className="h-5 w-5" /> {dineMode ? "Your table order" : "Your order"}</h2>
          {!dineMode && (
          <div className="flex gap-2 mb-3">
            <Button variant={fulfil === "COLLECTION" ? "default" : "outline"} className="flex-1 h-10" style={fulfil === "COLLECTION" ? { background: primary } : {}} data-testid="fulfil-COLLECTION" onClick={() => setFulfil("COLLECTION")}>Collection</Button>
            {data.config.delivery_enabled && <Button variant={fulfil === "DELIVERY" ? "default" : "outline"} className="flex-1 h-10" style={fulfil === "DELIVERY" ? { background: primary } : {}} data-testid="fulfil-DELIVERY" onClick={() => setFulfil("DELIVERY")}>Delivery</Button>}
          </div>
          )}
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {basket.length === 0 && <p className="text-sm text-muted-foreground">Add items from the menu.</p>}
            {basket.map((l) => {
              const flagged = lineFlagged(l);
              return (
                <div key={l.key} data-testid={`basket-line-${l.menu_item_id}`}
                  className={`flex items-center gap-2 text-sm rounded-lg transition-all ${flagged ? "ring-2 ring-red-500/60 bg-red-50 p-2" : ""}`}>
                  <span className="flex-1 min-w-0">
                    <span className="block truncate">{l.name}</span>
                    {l.options?.length > 0 && <span className="block text-[11px] text-muted-foreground truncate">{l.options.map((o) => o.value).join(", ")}</span>}
                    {flagged && (
                      <span className="block text-[11px] font-semibold text-red-600 truncate">
                        Contains: {(l.allergens || []).filter((a) => declaredAllergens.includes(a)).join(", ")}
                      </span>
                    )}
                  </span>
                  <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setQty(l.key, -1)}><Minus className="h-3 w-3" /></Button>
                  <span className="w-5 text-center">{l.quantity}</span>
                  <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setQty(l.key, 1)}><Plus className="h-3 w-3" /></Button>
                  <span className="w-14 text-right">{gbp(l.price * l.quantity)}</span>
                </div>
              );
            })}
          </div>

          {basketAllergens.length > 0 && (
            <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-3" data-testid="site-allergen-checklist">
              <p className="text-xs font-semibold text-amber-800 mb-2">⚠ Any allergies? Tap what to avoid — we'll flag it to the kitchen</p>
              <div className="flex flex-wrap gap-1.5">
                {basketAllergens.map((a) => {
                  const on = declaredAllergens.includes(a);
                  return (
                    <button key={a} type="button" data-testid={`site-allergen-${a}`} onClick={() => toggleDeclared(a)}
                      className={`h-8 px-3 rounded-full text-xs font-medium border transition-colors ${on ? "bg-red-600 text-white border-red-600" : "bg-white text-amber-900 border-amber-300 hover:border-red-400"}`}>
                      {a}
                    </button>
                  );
                })}
              </div>
              {declaredAllergens.length > 0 && (
                <button type="button" data-testid="site-allergen-clear" onClick={() => setDeclaredAllergens([])}
                  className="mt-2 text-[11px] text-amber-700 underline">Clear</button>
              )}
            </div>
          )}
          <div className="mt-3 space-y-2 border-t border-stone-200 pt-3">
            <Input placeholder={dineMode ? "Your name (optional)" : "Your name"} value={cust.name} onChange={(e) => setCust({ ...cust, name: e.target.value })} data-testid="order-name" />
            {!dineMode && <Input placeholder="Phone" value={cust.phone} onChange={(e) => setCust({ ...cust, phone: e.target.value })} data-testid="order-phone" />}
            {!dineMode && fulfil === "DELIVERY" && (<>
              <Input placeholder="Delivery address" value={cust.address} onChange={(e) => setCust({ ...cust, address: e.target.value })} data-testid="order-address" />
              <Input placeholder="Postcode" value={cust.postcode} onChange={(e) => setCust({ ...cust, postcode: e.target.value })} data-testid="order-postcode" />
            </>)}
            <Input placeholder="Allergies / dietary notes (optional)" value={cust.allergy} onChange={(e) => setCust({ ...cust, allergy: e.target.value })} data-testid="order-allergy" />
          </div>
          <div className="mt-3 text-sm space-y-1">
            <div className="flex justify-between"><span>Subtotal</span><span>{gbp(subtotal)}</span></div>
            {fee > 0 && <div className="flex justify-between text-muted-foreground"><span>Delivery</span><span>{gbp(fee)}</span></div>}
            <div className="flex justify-between font-bold text-base"><span>Total</span><span data-testid="site-total">{gbp(subtotal + fee)}</span></div>
          </div>
          <Button className="w-full h-12 mt-3" style={{ background: primary }} onClick={placeOrder} data-testid="site-place-order">{dineMode ? "Send order to kitchen" : "Place order"}</Button>
        </aside>
      </div>
      <footer className="text-center text-xs text-muted-foreground py-8">Powered by DineFlo</footer>
      {optItem && <OptionDialog item={optItem} primary={primary} onClose={() => setOptItem(null)} onConfirm={(opts, price) => add(optItem, opts, price)} />}
    </div>
  );
}
